import React, { useState, useRef, useEffect } from "react";

export type TimeoffEarningCodeType = {
    earnings_code?: string;
    earnings_code_name?: string;
};

interface EarningCodeDropdownProps {
    label: string;
    selectedEarningCode: TimeoffEarningCodeType | null;
    onSelectionChange: (selected: TimeoffEarningCodeType) => void;
    earningCodes: TimeoffEarningCodeType[];
    isLoading: boolean;
}

const EarningCodeDropdown: React.FC<EarningCodeDropdownProps> = ({
    label,
    selectedEarningCode,
    onSelectionChange,
    earningCodes,
    isLoading,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelection = (earningCode: TimeoffEarningCodeType) => {
        onSelectionChange(earningCode);
        setIsOpen(false);
    };

    return (
        <div className="relative flex-1" ref={dropdownRef}>
            <label className="block text-gray-700 font-semibold mb-2">{label}</label>

            {/* Selected Value */}
            <div
                className="relative min-w-[200px] border rounded-lg p-2 cursor-pointer bg-white flex items-center justify-between"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="truncate">
                    {selectedEarningCode ? selectedEarningCode.earnings_code_name : "Select earning code"}
                </span>
                <span className="ml-2">&#9662;</span> {/* Dropdown Arrow */}
            </div>

            {/* Dropdown Options */}
            {isOpen && (
                <div className="absolute z-10 flex flex-col mt-2 bg-white border rounded-lg shadow-lg min-w-[200px] max-w-[300px]">
                    {isLoading ? (
                        <div className="p-4 text-center">
                            <span className="animate-spin inline-block w-6 h-6 border-4 border-gray-300 border-t-blue-500 rounded-full"></span>
                        </div>
                    ) : (
                        <div className="absolute z-10 flex flex-col mt-2 bg-white border rounded-lg shadow-lg min-w-[200px] max-w-[300px]">
                            <div className="max-h-40 overflow-y-auto">
                                {earningCodes.length > 0 ? (
                                    earningCodes.map((earningCode) => (
                                        <div
                                            key={earningCode.earnings_code}
                                            className="p-2 cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSelection(earningCode)}
                                        >
                                            {earningCode.earnings_code_name}
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-2 text-gray-500 text-center">No earning codes available</div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default EarningCodeDropdown;
